import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import Document from "documents/document";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import DocumentRedlineSelection from "work/proposals/redlining/field-redlines/_views/document-redline-selection";
import { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import { Masked } from "work/_shared/masked-fields";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(() => ({
  flexWrap: "nowrap",
  minHeight: "20rem",
  height: "100%",
  width: "100%",
}));
const DocumentSelectorContainer = styled(ResizableBox)(() => ({
  display: "flex",
  position: "relative",
}));
const SelectedDocsList = styled(DocumentRedlineSelection)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  flex: 1,
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  position: "relative",
  "& .MuiListItem-root": {
    maxWidth: "550px",
  }
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));
const IssueContainer = styled("div")<{ active?: boolean }>(({ theme, active }) => ({
  animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
  margin: active ? theme.spacing(1) : "initial",
  marginBottom: theme.spacing(2),
  marginLeft: 0,
  marginTop: active ? 0 : theme.spacing(1),
  padding: active ? theme.spacing(1) : "initial",
  borderRadius: theme.shape.borderRadius,
}));
const DocumentGrid = styled(Grid)(() => ({
  flex: 1,
  overflowY: "scroll",
  minWidth: "25rem",
}));

type PoliciesTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  commentThreads: CommentThread[];
  commentsToolTip: string;
  disableEditing?: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
  onCommentsClicked: (field: ProposalField, name?: string) => void;
};

const PoliciesTab = (props: Readonly<PoliciesTabProps>) => {
  const {
    proposalRedline,
    activeReviewField,
    commentThreads,
    commentsToolTip,
    disableEditing,
    onProposalRedlineChange,
    onTraverseToNewField,
    onCommentsClicked,
  } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);
  const [documentTypeToUpload, setDocumentTypeToUpload] = React.useState<WorkDocumentType>();
  const [documentIdToReplace, setDocumentIdToReplace] = React.useState<Guid | null>(null);
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = React.useState<Guid | null>(null);

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Policies,
          ProposalField.VendorPolicies,
          ProposalField.ClientPolicies,
          ProposalField.VendorPolicy(issue.field.id ?? Guid.EMPTY),
          ProposalField.ClientPolicy(issue.field.id ?? Guid.EMPTY),
        ].includes(issue.field)
      ),
    [props.issues?.entries]
  );

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId?: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    if (!documentId) return;

    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const policyTemplate = await documentApiService.downloadDocument(documentId);
      await downloadFile(policyTemplate);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download policy template. Please try again", {
        variant: "error",
      });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  function handleDocumentRedlineChanged(
    newRedline: FieldRedlineArray<WorkDocument>,
    traversalFieldOverride?: ProposalField | null
  ) {
    let newProposalRedline: ProposalRedline;
    if (newRedline.field.isEqualTo(ProposalField.ClientPolicies)) {
      newProposalRedline = proposalRedline.updateClientPoliciesRedline(newRedline);
    } else if (newRedline.field.isEqualTo(ProposalField.VendorPolicies)) {
      newProposalRedline = proposalRedline.updateVendorPoliciesRedline(newRedline);
    } else {
      throw new Error("Unable to determine which policy redline to update.");
    }

    onProposalRedlineChange(newProposalRedline, traversalFieldOverride);
    onCommentsClicked(newRedline.field, undefined);
  }

  function handleDocumentSelectionChanged(selectedDocs: Document[], deselectedDocs: Document[]) {
    if (!selectedDocs.length && !deselectedDocs.length) return;
    // TODO: Is a full clone necessary here?
    let updatedProposalRedline = proposalRedline;

    for (const selectedDoc of selectedDocs) {
      const workDocument = WorkDocument.fromDocument(
        selectedDoc,
        session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
      );
      if (
        Masked.isMasked(updatedProposalRedline.clientPolicies) ||
        Masked.isMasked(updatedProposalRedline.vendorPolicies)
      ) {
        throw new Error("You do not have permission to modify contracts.");
      }
      if (
        !session.context?.viewingAsVendor &&
        !updatedProposalRedline.clientPolicies.redlines.some((document) =>
          document.currentEntry?.is(workDocument)
        )
      ) {
        updatedProposalRedline = updatedProposalRedline.updateClientPoliciesRedline(
          updatedProposalRedline.clientPolicies.addEntry(workDocument)
        );
      }

      if (
        Masked.isMasked(updatedProposalRedline.clientPolicies) ||
        Masked.isMasked(updatedProposalRedline.vendorPolicies)
      ) {
        throw new Error("You do not have permission to modify contracts.");
      }
      if (
        session.context?.viewingAsVendor &&
        !updatedProposalRedline.vendorPolicies.redlines.some((document) =>
          document.currentEntry?.is(workDocument)
        )
      ) {
        updatedProposalRedline = updatedProposalRedline.updateVendorPoliciesRedline(
          updatedProposalRedline.vendorPolicies.addEntry(workDocument)
        );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      if (
        Masked.isMasked(updatedProposalRedline.clientPolicies) ||
        Masked.isMasked(updatedProposalRedline.vendorPolicies)
      ) {
        throw new Error("You do not have permission to modify contracts.");
      }
      if (
        !session.context?.viewingAsVendor &&
        updatedProposalRedline.clientPolicies.redlines.some((document) =>
          document.currentEntry?.id.isEqualTo(deselectedDoc.id)
        )
      ) {
        const redlineId = updatedProposalRedline.clientPolicies.redlines.find(
          (documentRedline) =>
            documentRedline.currentEntry?.id?.isEqualTo(deselectedDoc.id) ||
            (!documentRedline.currentEntry && documentRedline.revisedEntry?.id?.isEqualTo(deselectedDoc.id))
        )?.field.id;

        if (!redlineId) {
          console.error("Unable to determine which policy redline to update.");
          continue;
        }

        updatedProposalRedline = updatedProposalRedline.updateClientPoliciesRedline(
          updatedProposalRedline.clientPolicies.removeEntryByFieldId(redlineId)
        );
      }

      if (
        Masked.isMasked(updatedProposalRedline.clientPolicies) ||
        Masked.isMasked(updatedProposalRedline.vendorPolicies)
      ) {
        throw new Error("You do not have permission to modify contracts.");
      }
      if (
        session.context?.viewingAsVendor &&
        updatedProposalRedline.vendorPolicies.redlines.some((document) =>
          document.currentEntry?.id.isEqualTo(deselectedDoc.id)
        )
      ) {
        const redlineId = updatedProposalRedline.vendorPolicies.redlines.find(
          (documentRedline) =>
            documentRedline.currentEntry?.id?.isEqualTo(deselectedDoc.id) ||
            (!documentRedline.currentEntry && documentRedline.revisedEntry?.id?.isEqualTo(deselectedDoc.id))
        )?.field.id;
        if (!redlineId) {
          console.error("Unable to determine which policy redline to update.");
          continue;
        }
        updatedProposalRedline = updatedProposalRedline.updateVendorPoliciesRedline(
          updatedProposalRedline.vendorPolicies.removeEntryByFieldId(redlineId)
        );
      }
    }

    let updatedDocumentRedline = session.context?.viewingAsVendor
      ? updatedProposalRedline.vendorPolicies
      : updatedProposalRedline.clientPolicies;

    if (Masked.isMasked(updatedDocumentRedline)) {
      throw new Error("You do not have permission to modify contracts.");
    }
    if (session.context?.viewingAsVendor) {
      updatedProposalRedline = proposalRedline.updateVendorPoliciesRedline(updatedDocumentRedline);
    } else {
      updatedProposalRedline = proposalRedline.updateClientPoliciesRedline(updatedDocumentRedline);
    }

    onProposalRedlineChange(updatedProposalRedline, null);
    onCommentsClicked(
      session.context?.viewingAsVendor ? ProposalField.VendorPolicies : ProposalField.ClientPolicies,
      undefined
    );
  }

  async function handleBeginCreateDocumentFromTemplate(templateId: Guid | undefined | null) {
    if (!templateId) return;
    setDocumentTypeToUpload(
      session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
    );
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }

  function renderDocumentSelector() {
    const documentsRedline = session.context?.viewingAsVendor
      ? proposalRedline.vendorPolicies
      : proposalRedline.clientPolicies;
    if (Masked.isMasked(documentsRedline)) {
      throw new Error("You do not have permission to view contracts.");
    }
    let selectedTemplateIds: Guid[] = [];
    for (const redline of documentsRedline.redlines) {
      if (redline.currentEntry?.templateIds) {
        selectedTemplateIds = selectedTemplateIds.concat(redline.currentEntry.templateIds);
      }
    }
    return (
      <DocumentSelector
        documentType="policy"
        selectedDocumentIds={documentsRedline.currentIds}
        selectedTemplateIds={selectedTemplateIds}
        onDocumentSelectionChanged={handleDocumentSelectionChanged}
        onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
      />
    );
  }

  async function handleBeginReplaceDocument(documentId: Guid | undefined | null, documentType: WorkDocumentType) {
    if (!documentId) return;

    setDocumentTypeToUpload(documentType);
    setDocumentIdToReplace(documentId);
    await handleDownloadDocumentById(documentId);
  }

  function handlePolicyDocumentAdded(document: Document | UnavailableDocument) {
    if (document instanceof UnavailableDocument) {
      throw new TypeError("Document must be available");
    }

    let updatedProposalRedline = proposalRedline;

    if (
      Masked.isMasked(proposalRedline.vendorPolicies) ||
      Masked.isMasked(proposalRedline.clientPolicies) ||
      Masked.isMasked(updatedProposalRedline.vendorPolicies) ||
      Masked.isMasked(updatedProposalRedline.clientPolicies)
    ) {
      throw new Error("You do not have permission to modify contracts.");
    }
    const workDocument = WorkDocument.fromDocument(
      document,
      documentTypeToUpload ?? session.context?.viewingAsVendor
        ? WorkDocumentType.VendorPolicy
        : WorkDocumentType.ClientPolicy,
      documentIdToReplace ?? undefined
    );
    if (documentIdToReplace && documentTypeToUpload === WorkDocumentType.VendorPolicy) {
      updatedProposalRedline = proposalRedline.updateVendorPoliciesRedline(
        proposalRedline.vendorPolicies.replaceEntryById(documentIdToReplace, workDocument)
      );
    } else if (documentIdToReplace && documentTypeToUpload === WorkDocumentType.ClientPolicy) {
      updatedProposalRedline = proposalRedline.updateClientPoliciesRedline(
        proposalRedline.clientPolicies.replaceEntryById(documentIdToReplace, workDocument)
      );
    } else if (documentTypeToUpload === WorkDocumentType.VendorPolicy) {
      updatedProposalRedline = proposalRedline.updateVendorPoliciesRedline(
        proposalRedline.vendorPolicies.addEntry(workDocument)
      );
    } else if (documentTypeToUpload === WorkDocumentType.ClientPolicy) {
      updatedProposalRedline = proposalRedline.updateClientPoliciesRedline(
        proposalRedline.clientPolicies.addEntry(workDocument)
      );
    }

    onProposalRedlineChange(updatedProposalRedline, null);
    onCommentsClicked(
      documentTypeToUpload === WorkDocumentType.VendorPolicy
        ? ProposalField.VendorPolicies
        : ProposalField.ClientPolicies,
      undefined
    );
    setDocumentIdToReplace(null);
    setCreatingDocumentFromTemplate(null);
  }

  if (Masked.isMasked(proposalRedline.clientPolicies) || Masked.isMasked(proposalRedline.vendorPolicies))
    return "You do not have permission to view contracts.";
  return (
    <>
      {(issues?.length ?? 0) > 0 && (
        <IssueContainer>
          {issues?.map((issue) => (
            <Typography
              key={issue.field?.cacheKey}
              color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
              variant="body1"
            >
              {issue.description}
            </Typography>
          ))}
        </IssueContainer>
      )}
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <DocumentSelectorContainer
              resizeHandles={["e"]}
              axis='x'
              width={450}
              height={Infinity}
              minConstraints={[375, Infinity]}
            >
              {renderDocumentSelector()}
            </DocumentSelectorContainer>
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          </>
        )}
        <DocumentGrid size={4}>
          <SelectedDocsList
            workDocumentType={WorkDocumentType.ClientPolicy}
            documentsRedline={proposalRedline.clientPolicies}
            accountType={AccountType.Client}
            downloadingFileId={isDownloadingFile}
            disableEditing={disableEditing}
            activeReviewField={activeReviewField}
            onDownload={handleDownloadDocumentById}
            onDocumentRedlineChange={(newRedline, traversalFieldOverride) => {
              try {
                handleDocumentRedlineChanged(newRedline, traversalFieldOverride);
              } catch (error) {
                console.error(error);
                enqueueSnackbar("Failed to update policy redline.", {
                  variant: "error",
                });
              }
            }}
            onBeginReplaceDocument={(docId) => handleBeginReplaceDocument(docId, WorkDocumentType.ClientPolicy)}
            onDocumentClicked={(documentId: Guid) => {
              onTraverseToNewField?.(ProposalField.ClientPolicy(documentId));
            }}
            onTraverseToNewField={onTraverseToNewField}
            commentThreads={commentThreads}
            commentsToolTip={commentsToolTip}
            onCommentsClicked={onCommentsClicked}
          />
          <Divider />
          <SelectedDocsList
            workDocumentType={WorkDocumentType.VendorPolicy}
            documentsRedline={proposalRedline.vendorPolicies}
            accountType={AccountType.Vendor}
            downloadingFileId={isDownloadingFile}
            commentThreads={commentThreads}
            commentsToolTip={commentsToolTip}
            disableEditing={disableEditing}
            activeReviewField={activeReviewField}
            onCommentsClicked={onCommentsClicked}
            onDownload={handleDownloadDocumentById}
            onDocumentRedlineChange={(newRedline, traversalFieldOverride) => {
              try {
                handleDocumentRedlineChanged(newRedline, traversalFieldOverride);
              } catch (error) {
                console.error(error);
                enqueueSnackbar("Failed to update policy redline.", {
                  variant: "error",
                });
              }
            }}
            onBeginReplaceDocument={(docId) => handleBeginReplaceDocument(docId, WorkDocumentType.VendorPolicy)}
            onDocumentClicked={(documentId: Guid) => {
              onTraverseToNewField?.(ProposalField.VendorPolicy(documentId));
            }}
            onTraverseToNewField={onTraverseToNewField}
          />
        </DocumentGrid>
      </ListContainer>
      {documentTypeToUpload && (creatingDocumentFromTemplate || documentIdToReplace) && (
        <Uploader
          session={session}
          documentType={documentTypeToUpload}
          templateId={creatingDocumentFromTemplate ?? undefined}
          onDocumentUploaded={handlePolicyDocumentAdded}
          onClose={() => {
            setCreatingDocumentFromTemplate(null);
            setDocumentIdToReplace(null);
          }}
        />
      )}
    </>
  );
};

export default PoliciesTab;
